import jwtDecode from 'jwt-decode'
import { createContext, useContext, useEffect, useState } from 'react'
import { isValidToken, refreshAccessToken, setSession } from '../utils/jwt'
import React from 'react'
// other imports

/**
 * @typedef {Object} TAuth
 * @property {string} token
 * @property {boolean} pendingAuth
 * @property {boolean} isAuthenticated
 * @property {boolean} isForbidden
 * @property {(accessToken: string) => void} authenticate
 */

/**
 * @type {React.Context<TAuth>}
 */
const Context = createContext()
const REQUIRED_ROLE = 'oli-community-operator'

const hasRequiredRole = (accessToken) => {
  if (!accessToken) return false

  try {
    const payload = jwtDecode(accessToken)
    const communityRoles = payload?.resource_access?.['community-app']?.roles
    return Array.isArray(communityRoles) && communityRoles.includes(REQUIRED_ROLE)
  } catch (error) {
    return false
  }
}

const AuthProvider = ({ children }) => {
  const [pendingAuth, setPendingAuth] = useState(true)
  const [token, setToken] = useState('')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isForbidden, setIsForbidden] = useState(false)
  const [refreshToken, setRefreshToken] = useState('')
  const origin = 'dashboard'
  // Authenticate Users
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('accessToken')
      const storedRefreshToken = localStorage.getItem('refreshToken')

      if (storedToken) {
        setRefreshToken(storedRefreshToken)
        setToken(storedToken)

        try {
          const isTokenValid = isValidToken(storedToken)
          const userHasRequiredRole = hasRequiredRole(storedToken)

          if (isTokenValid) {
            if (userHasRequiredRole) {
              // Token is valid and role is present, user is authenticated
              setIsAuthenticated(true)
              setIsForbidden(false)
            } else {
              // Token is valid but role is missing
              setSession(null, null)
              setToken('')
              setRefreshToken('')
              setIsAuthenticated(false)
              setIsForbidden(true)
            }
            setPendingAuth(false)
          } else if (storedRefreshToken) {
            // Token is expired but we have a refresh token, try to refresh
            try {
              const { access_token, refresh_token } = await refreshAccessToken(
                storedRefreshToken
              )
              const refreshedTokenHasRequiredRole = hasRequiredRole(access_token)

              if (refreshedTokenHasRequiredRole) {
                setToken(access_token)
                setRefreshToken(refresh_token)
                setIsAuthenticated(true)
                setIsForbidden(false)
              } else {
                setSession(null, null)
                setToken('')
                setRefreshToken('')
                setIsAuthenticated(false)
                setIsForbidden(true)
              }
            } catch (refreshError) {
              // Refresh failed, clear session
              setSession(null, null)
              setToken('')
              setRefreshToken('')
              setIsAuthenticated(false)
              setIsForbidden(false)
            } finally {
              setPendingAuth(false)
            }
          } else {
            // Token is expired and no refresh token
            setSession(null, null)
            setToken('')
            setRefreshToken('')
            setIsAuthenticated(false)
            setIsForbidden(isTokenValid && !userHasRequiredRole)
            setPendingAuth(false)
          }
        } catch (error) {
          // Invalid token format
          setSession(null, null)
          setToken('')
          setRefreshToken('')
          setIsAuthenticated(false)
          setIsForbidden(false)
          setPendingAuth(false)
        }
      } else {
        // No token stored
        setPendingAuth(false)
        setIsAuthenticated(false)
        setIsForbidden(false)
      }
    }

    initializeAuth()
  }, [])

  const authenticate = (accessToken, refreshTokenParam) => {
    try {
      const accessTokenHasRequiredRole = hasRequiredRole(accessToken)

      if (isValidToken(accessToken)) {
        if (accessTokenHasRequiredRole) {
          setToken(accessToken)
          setRefreshToken(refreshTokenParam)
          setSession(accessToken, refreshTokenParam)
          setIsAuthenticated(true)
          setIsForbidden(false)
        } else {
          setToken('')
          setRefreshToken('')
          setSession(null, null)
          setIsAuthenticated(false)
          setIsForbidden(true)
        }
      } else {
        // If access token is invalid, try to refresh if refresh token exists
        if (refreshTokenParam) {
          refreshAccessToken(refreshTokenParam)
            .then(({ access_token, refresh_token }) => {
              if (hasRequiredRole(access_token)) {
                setToken(access_token)
                setRefreshToken(refresh_token)
                setSession(access_token, refresh_token)
                setIsAuthenticated(true)
                setIsForbidden(false)
              } else {
                setToken('')
                setRefreshToken('')
                setSession(null, null)
                setIsAuthenticated(false)
                setIsForbidden(true)
              }
            })
            .catch(() => {
              setToken('')
              setRefreshToken('')
              setSession(null, null)
              setIsAuthenticated(false)
              setIsForbidden(false)
            })
        } else {
          setToken('')
          setRefreshToken('')
          setSession(null, null)
          setIsAuthenticated(false)
          setIsForbidden(isValidToken(accessToken) && !accessTokenHasRequiredRole)
        }
      }
    } catch (error) {
      setToken('')
      setRefreshToken('')
      setSession(null, null)
      setIsAuthenticated(false)
      setIsForbidden(false)
    }
  }

  return (
    <Context.Provider
      value={{
        token,
        refreshToken,
        origin,
        pendingAuth,
        isAuthenticated,
        isForbidden,
        authenticate,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(Context)

  if (!context) {
    throw new Error(`useAuth() was called outside a AuthProvider`)
  }

  return context
}

export default AuthProvider
